// frontend/src/components/Events/EventNewsCarousel.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  alpha,
  useTheme,
  Button
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Folder as FolderIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { newsAPI } from '../../services/eventsAPI';
import CreateEditNewsModal from './CreateEditNewsModal';
import NewsDetailModal from './NewsDetailModal';
import AllNewsModal from './AllNewsModal';
import NewsCard from './NewsCard';

const NewsSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(-15),
  zIndex: 5,
  padding: theme.spacing(3, 2, 2),
  paddingTop: theme.spacing(15),
  borderRadius: 30,

  // iOS 26/27 Liquid Glass — цветные линзы страницы просвечивают сквозь поверхность.
  background: 'linear-gradient(135deg, rgba(255,255,255,0.62), rgba(255,255,255,0.42))',
  border: '1px solid rgba(255,255,255,0.78)',
  boxShadow: '0 18px 45px rgba(35,45,55,0.06), inset 0 1px 0 rgba(255,255,255,0.88)',
  backdropFilter: 'blur(12px) saturate(165%)',
  WebkitBackdropFilter: 'blur(12px) saturate(165%)',
  overflow: 'hidden',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1.2),
  padding: theme.spacing(0, 1),
}));

const AddNewsButton = styled(IconButton)(({ theme }) => ({
  width: 38,
  height: 38,
  color: theme.palette.primary.main,
  background: alpha(theme.palette.primary.main, 0.10),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  boxShadow: `0 7px 18px ${alpha(theme.palette.primary.main, 0.10)}`,
  transition: 'transform 0.25s ease, background 0.25s ease',
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.16),
    transform: 'translateY(-2px) scale(1.03)',
  },
}));

const ScrollViewport = styled(Box)(() => ({
  position: 'relative',
  // Больше нет белых градиентных "штор" по краям карусели.
}));

const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: theme.spacing(1.8),
  padding: theme.spacing(1.5, 1, 2.2),
  scrollBehavior: 'smooth',
  scrollSnapType: 'x proximity',
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': {
    height: 6,
    background: 'transparent',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.10),
    borderRadius: 999,
  },
  '&:hover::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.22),
  },
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-58%)',
  width: 46,
  height: 46,
  zIndex: 12,
  color: theme.palette.primary.main,
  background: alpha('#FFFFFF', 0.64),
  border: `1px solid ${alpha('#FFFFFF', 0.82)}`,
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.10)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.9)}`,
  transition: 'transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease',
  '&:hover': {
    background: alpha('#FFFFFF', 0.82),
    transform: 'translateY(-58%) scale(1.06)',
    boxShadow: `0 14px 32px ${alpha(theme.palette.common.black, 0.14)}`,
  },
}));

const AllNewsCard = styled(Paper)(({ theme }) => ({
  minWidth: 220,
  maxWidth: 220,
  minHeight: 245,
  margin: theme.spacing(0.5),
  borderRadius: 26,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  background: `linear-gradient(145deg, ${alpha('#FFFFFF', 0.68)}, ${alpha(theme.palette.primary.main, 0.08)})`,
  border: `1px dashed ${alpha(theme.palette.primary.main, 0.28)}`,
  boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.06)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.78)}`,
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease, background 0.3s ease',
  '&:hover': {
    background: `linear-gradient(145deg, ${alpha('#FFFFFF', 0.80)}, ${alpha(theme.palette.primary.main, 0.13)})`,
    transform: 'translateY(-5px)',
    boxShadow: `0 18px 38px ${alpha(theme.palette.common.black, 0.10)}`,
  },
}));

const EmptyNewsState = styled(Paper)(({ theme }) => ({
  minHeight: 220,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 26,
  background: alpha('#FFFFFF', 0.34),
  border: `1px dashed ${alpha(theme.palette.primary.main, 0.18)}`,
  boxShadow: 'none',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
}));

const EventNewsCarousel = ({
  news = [],
  eventId,
  sections = [],
  currentSectionId = null,
  canCreateNews = false,
  onNewsUpdate,
  likedNews = {},
  onLikeToggle
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const scrollRef = useRef(null);

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [allNewsModalOpen, setAllNewsModalOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);

  const displayedNews = news.slice(0, 15);
  const hasMoreNews = news.length > 15;

  useEffect(() => {
    const checkScroll = () => {
      const container = scrollRef.current;
      if (!container) return;

      setShowLeftArrow(container.scrollLeft > 8);
      setShowRightArrow(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);

    const container = scrollRef.current;
    if (container) container.addEventListener('scroll', checkScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', checkScroll);
      if (container) container.removeEventListener('scroll', checkScroll);
    };
  }, [displayedNews]);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -350, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 350, behavior: 'smooth' });
    }
  };

  const handleNewsClick = async (newsItem) => {
    setSelectedNews(newsItem);
    setDetailModalOpen(true);

    try {
      await newsAPI.incrementView(newsItem.id);
      if (onNewsUpdate) onNewsUpdate();
    } catch (err) {
      console.error('Ошибка при увеличении просмотров:', err);
    }
  };

  const handleLikeWrapper = (newsId, e) => {
    onLikeToggle(newsId, e);
  };

  const handleCreateSuccess = () => {
    if (onNewsUpdate) onNewsUpdate();
    setCreateModalOpen(false);
  };

  const handleDetailClose = (updated) => {
    setDetailModalOpen(false);
    setSelectedNews(null);
    if (updated && onNewsUpdate) onNewsUpdate();
  };

  const handleDeleteFromDetail = () => {
    if (onNewsUpdate) onNewsUpdate();
    setDetailModalOpen(false);
    setSelectedNews(null);
  };

  if (news.length === 0 && !canCreateNews) {
    return (
      <NewsSection>
        <SectionHeader>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 750,
              letterSpacing: '-0.02em',
              color: '#14284A',
            }}
          >
            Новости мероприятия
          </Typography>
        </SectionHeader>

        <EmptyNewsState>
          <FolderIcon
            sx={{
              fontSize: 38,
              color: alpha(theme.palette.primary.main, 0.30),
              mb: 1,
            }}
          />
          <Typography sx={{ fontWeight: 700, color: '#536985', mb: 0.35 }}>
            Пока нет новостей
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Новости мероприятия появятся здесь
          </Typography>
        </EmptyNewsState>
      </NewsSection>
    );
  }

  return (
    <NewsSection>
      <SectionHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 750,
              letterSpacing: '-0.02em',
              color: '#14284A',
            }}
          >
            Новости мероприятия
          </Typography>
          {news.length > 0 && (
            <Chip
              label={news.length}
              size="small"
              sx={{
                height: 24,
                bgcolor: alpha(theme.palette.primary.main, 0.09),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                fontWeight: 700,
                '& .MuiChip-label': { px: 0.9 },
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {canCreateNews && (
            <AddNewsButton onClick={() => setCreateModalOpen(true)} aria-label="Добавить новость">
              <AddIcon />
            </AddNewsButton>
          )}

          {news.length > 0 && (
            <Button
              variant="text"
              onClick={() => setAllNewsModalOpen(true)}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
              sx={{
                borderRadius: 999,
                px: 1.2,
                color: theme.palette.primary.main,
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.07),
                },
              }}
            >
              Все новости
            </Button>
          )}
        </Box>
      </SectionHeader>

      <Box sx={{ position: 'relative' }}>
        {showLeftArrow && (
          <NavButton
            onClick={scrollLeft}
            sx={{ left: -13 }}
            size="small"
            aria-label="Предыдущие новости"
          >
            <ChevronLeftIcon />
          </NavButton>
        )}

        {showRightArrow && (
          <NavButton
            onClick={scrollRight}
            sx={{ right: -13 }}
            size="small"
            aria-label="Следующие новости"
          >
            <ChevronRightIcon />
          </NavButton>
        )}

        {news.length === 0 ? (
          <EmptyNewsState>
            <Typography sx={{ fontWeight: 700, color: '#536985' }} gutterBottom>
              Пока нет новостей
            </Typography>
            {canCreateNews && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateModalOpen(true)}
                sx={{ mt: 1.5, borderRadius: 999, textTransform: 'none' }}
              >
                Создать первую новость
              </Button>
            )}
          </EmptyNewsState>
        ) : (
          <ScrollViewport>
            <ScrollContainer ref={scrollRef}>
              {displayedNews.map((item) => (
                <NewsCard
                  key={item.id}
                  news={item}
                  sections={sections}
                  isLiked={likedNews[item.id]}
                  onLikeToggle={handleLikeWrapper}
                  onClick={() => handleNewsClick(item)}
                />
              ))}

              {hasMoreNews && (
                <AllNewsCard onClick={() => setAllNewsModalOpen(true)}>
                  <Typography
                    sx={{
                      fontSize: '2.8rem',
                      lineHeight: 1,
                      fontWeight: 300,
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    +{news.length - 15}
                  </Typography>
                  <Typography sx={{ fontWeight: 750, color: theme.palette.primary.main }}>
                    Все новости
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {news.length} всего
                  </Typography>
                  <ArrowForwardIcon
                    className="news-arrow"
                    sx={{ mt: 1.5, color: theme.palette.primary.main, transition: 'transform 0.25s ease' }}
                  />
                </AllNewsCard>
              )}
            </ScrollContainer>
          </ScrollViewport>
        )}
      </Box>

      <CreateEditNewsModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        eventId={eventId}
        sections={sections}
        currentSectionId={currentSectionId}
        onSuccess={handleCreateSuccess}
      />

      {selectedNews && (
        <NewsDetailModal
          open={detailModalOpen}
          onClose={handleDetailClose}
          news={selectedNews}
          onDelete={handleDeleteFromDetail}
          canEdit={selectedNews.can_edit}
          canDelete={selectedNews.can_delete}
          isLiked={likedNews[selectedNews.id]}
          onLikeToggle={onLikeToggle}
        />
      )}

      <AllNewsModal
        open={allNewsModalOpen}
        onClose={() => setAllNewsModalOpen(false)}
        news={news}
        sections={sections}
        likedNews={likedNews}
        onLikeToggle={onLikeToggle}
        onNewsClick={handleNewsClick}
      />
    </NewsSection>
  );
};

export default EventNewsCarousel;
